import React, { useCallback, useEffect, useState } from "react";

import { getDigitalAgronomistMainCrops } from "services";

import { useCancelAsyncOnUnmount, useGetDigitalAgronomistPreferences, useNavigate, useTranslation, useUserContext } from "utils";

import { CoverCropWithScores, MainCropOption } from "models";

import { Button, DATable, Flex, Icon, IconSizes, Typography } from "components";

import { DigitalAgronomistDrawer } from "pages/app/digital-agronomist/components/DigitalAgronomistDrawer.tsx";
import {
  convertToMainCropData,
  generateCoverCropsColumns,
  generateMainCropsColumns,
  transformCoverCropsResponseToTableData,
} from "pages/app/digital-agronomist/utils.tsx";

export default function Timeline() {
  const signal = useCancelAsyncOnUnmount()();
  const { t } = useTranslation("digitalAgronomist");
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { data: cropRecommendationsResponse, isFetching } = useGetDigitalAgronomistPreferences({ user });
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [drawerDetail, setDrawerDetail] = useState<CoverCropWithScores>();
  const [mainCropsList, setMainCropsList] = useState<MainCropOption[]>([]);
  const [mainCropYearToAddOrUpdate, setMainCropYearToAddOrUpdate] = useState<number | null>(null);
  const [expandedCoverCropsTableRowKeys, setExpandedCoverCropsTableRowKeys] = useState<number[]>([0]);
  const [recommendationExist, setRecommendationExist] = useState<boolean>();

  const handleDrawerClick = (coverCropsWithScores: CoverCropWithScores) => {
    setShowDrawer(true);
    setDrawerDetail(coverCropsWithScores);
  };

  const checkForExistingRecommendations = useCallback(() => {
    if (!cropRecommendationsResponse) return;
    const existingRecommendations = cropRecommendationsResponse.cropRecommendations.some((recommendation) => recommendation.coverCropsWithScores.length > 0);
    setRecommendationExist(existingRecommendations);
  }, [cropRecommendationsResponse]);

  const fetchMainCropsList = useCallback(async () => {
    const cropsList = await getDigitalAgronomistMainCrops({ signal });
    setMainCropsList(cropsList);
  }, [signal]);

  useEffect(() => {
    checkForExistingRecommendations();
  }, [checkForExistingRecommendations]);

  useEffect(() => {
    fetchMainCropsList();
  }, [fetchMainCropsList]);

  if (!cropRecommendationsResponse?.cropRecommendations.length) return;
  return (
    <div className="margin-top-200">
      <>
        <Typography variant="h5" className="margin-left-75 margin-bottom-75">
          {t("table.title")}
        </Typography>
        <DATable
          className="timeline main-crops margin-bottom-300"
          columns={generateMainCropsColumns(
            cropRecommendationsResponse,
            mainCropsList,
            mainCropYearToAddOrUpdate,
            setMainCropYearToAddOrUpdate,
            cropRecommendationsResponse.cropRecommendations.length
          )}
          dataSource={convertToMainCropData(cropRecommendationsResponse, t)}
          pagination={false}
          loading={isFetching}
          scroll={{ y: 55 * 5 }}
        />
        {recommendationExist ? (
          <>
            <Typography variant="h5" className="margin-left-75 margin-bottom-100">
              {t("recommendationTable.title")}
            </Typography>
            <DATable
              className="timeline cover-crops margin-bottom-300"
              columns={generateCoverCropsColumns(cropRecommendationsResponse, t)}
              expandable={{
                expandedRowKeys: expandedCoverCropsTableRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) {
                    setExpandedCoverCropsTableRowKeys([...expandedCoverCropsTableRowKeys, record.key]);
                    return;
                  }

                  setExpandedCoverCropsTableRowKeys(expandedCoverCropsTableRowKeys.filter((key) => key !== record.key));
                },
              }}
              dataSource={transformCoverCropsResponseToTableData(cropRecommendationsResponse, t, handleDrawerClick)}
              pagination={false}
              loading={isFetching}
              scroll={{ y: 55 * 5 }}
            />
          </>
        ) : !isFetching ? (
          <NoContent message={t("noContent")} />
        ) : null}

        <DigitalAgronomistDrawer showDrawer={showDrawer} setShowDrawer={setShowDrawer} coverCropWithScores={drawerDetail} />
        {recommendationExist ? (
          <BestNextActionBanner
            title={t("bestNextActionBanner.title")}
            description={t("bestNextActionBanner.description")}
            iconName="klim/graph"
            iconSize="xl"
            ctaLabel={t("bestNextActionBanner.cta")}
            onClick={() => navigate("/crop-explorer")}
          />
        ) : null}
      </>
    </div>
  );
}

const BestNextActionBanner = ({
  title,
  description,
  iconName,
  iconSize = "xl",
  ctaLabel,
  onClick,
}: {
  title: string;
  description: string;
  iconName: string;
  iconSize?: IconSizes;
  ctaLabel: string;
  onClick: () => void;
}) => (
  <Flex className="best-next-action-banner-container" justify="space-between" align="center" gap="2rem">
    <Flex gap="1.5rem" align="center">
      <Icon iconName={iconName} size={iconSize} />
      <div>
        <Typography variant="h5">{title}</Typography>
        {description && (
          <Typography variant="paragraph-m" className="margin-0">
            {description}
          </Typography>
        )}
      </div>
    </Flex>
    <Button className="best-next-action-banner-button" type="primary" onClick={onClick}>
      {ctaLabel}
    </Button>
  </Flex>
);

const NoContent = ({ message }: { message: string }) => (
  <Flex vertical={true} align="center" justify="center">
    <Icon iconName="tabler/zoom-cancel" size="xl" />
    <Typography variant="paragraph-m">{message}</Typography>
  </Flex>
);
